import { apiClient } from '../axiosInstance';

class NotificationService {
  constructor() {
    this.baseUrl = '/notifications';
  }

  // ============================================
  // USER NOTIFICATION ENDPOINTS
  // ============================================

  /**
   * Get user notifications with pagination
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=10] - Items per page
   * @param {string} [params.status] - Filter by status (unread, read, archived, deleted)
   * @param {string} [params.type] - Filter by type (info, success, warning, error, promotion, order, account, system)
   * @param {boolean} [params.unreadOnly] - Filter unread only
   */
  async getUserNotifications(params = {}) {
    return apiClient.get(this.baseUrl, { params });
  }

  /**
   * Get unread notification count for current user
   */
  async getUnreadCount() {
    return apiClient.get(`${this.baseUrl}/unread-count`);
  }

  /**
   * Get recent notifications for current user
   * @param {number} [limit=10] - Number of notifications to return
   */
  async getRecentNotifications(limit = 10) {
    return apiClient.get(`${this.baseUrl}/recent`, { params: { limit } });
  }

  /**
   * Get notification by ID
   * @param {number} id - Notification ID
   */
  async getNotificationById(id) {
    return apiClient.get(`${this.baseUrl}/${id}`);
  }

  /**
   * Mark notification as read
   * @param {number} id - Notification ID
   */
  async markAsRead(id) {
    return apiClient.patch(`${this.baseUrl}/${id}/read`);
  }

  /**
   * Mark notification as archived
   * @param {number} id - Notification ID
   */
  async markAsArchived(id) {
    return apiClient.patch(`${this.baseUrl}/${id}/archive`);
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    return apiClient.post(`${this.baseUrl}/mark-all-read`);
  }

  /**
   * Delete notification
   * @param {number} id - Notification ID
   */
  async deleteNotification(id) {
    return apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Bulk delete notifications
   * @param {number[]} ids - Array of notification IDs
   */
  async bulkDeleteNotifications(ids) {
    return apiClient.post(`${this.baseUrl}/bulk-delete`, { ids });
  }

  // ============================================
  // ADMIN NOTIFICATION ENDPOINTS
  // ============================================

  /**
   * Get admin notifications with pagination
   * @param {Object} params - Query parameters
   */
  async getAdminNotifications(params = {}) {
    return apiClient.get(`${this.baseUrl}/admin/notifications`, { params });
  }

  /**
   * Get unread notification count for admin
   */
  async getAdminUnreadCount() {
    return apiClient.get(`${this.baseUrl}/admin/notifications/unread-count`);
  }

  /**
   * Get recent admin notifications
   * @param {number} [limit=10] - Number of notifications to return
   */
  async getRecentAdminNotifications(limit = 10) {
    return apiClient.get(`${this.baseUrl}/admin/notifications/recent`, { params: { limit } });
  }

  /**
   * Get admin notification by ID
   * @param {number} id - Notification ID
   */
  async getAdminNotificationById(id) {
    return apiClient.get(`${this.baseUrl}/admin/notifications/${id}`);
  }

  /**
   * Mark admin notification as read
   * @param {number} id - Notification ID
   */
  async markAdminAsRead(id) {
    return apiClient.patch(`${this.baseUrl}/admin/notifications/${id}/read`);
  }

  /**
   * Mark admin notification as archived
   * @param {number} id - Notification ID
   */
  async markAdminAsArchived(id) {
    return apiClient.patch(`${this.baseUrl}/admin/notifications/${id}/archive`);
  }

  /**
   * Mark all admin notifications as read
   */
  async markAllAdminAsRead() {
    return apiClient.post(`${this.baseUrl}/admin/notifications/mark-all-read`);
  }

  /**
   * Delete admin notification
   * @param {number} id - Notification ID
   */
  async deleteAdminNotification(id) {
    return apiClient.delete(`${this.baseUrl}/admin/notifications/${id}`);
  }

  // ============================================
  // SUPER ADMIN NOTIFICATION ENDPOINTS
  // ============================================

  /**
   * Create notification for a specific user (Super Admin only)
   * @param {number} userId - Target user ID
   * @param {Object} data - Notification data
   */
  async createUserNotification(userId, data) {
    return apiClient.post(`${this.baseUrl}/admin/notifications/user/${userId}`, data);
  }

  /**
   * Create notification for multiple users (Super Admin only)
   * @param {Object} data - Notification data with userIds array
   */
  async createBulkUserNotifications(data) {
    return apiClient.post(`${this.baseUrl}/admin/notifications/users/bulk`, data);
  }

  /**
   * Create notification for all users (Super Admin only)
   * @param {Object} data - Notification data
   */
  async createAllUsersNotification(data) {
    return apiClient.post(`${this.baseUrl}/admin/notifications/users/all`, data);
  }

  /**
   * Create notification for a specific admin (Super Admin only)
   * @param {number} adminId - Target admin ID
   * @param {Object} data - Notification data
   */
  async createAdminNotification(adminId, data) {
    return apiClient.post(`${this.baseUrl}/admin/notifications/admin/${adminId}`, data);
  }

  /**
   * Get notification statistics (Super Admin only)
   */
  async getNotificationStats() {
    return apiClient.get(`${this.baseUrl}/admin/notifications/stats`);
  }

  /**
   * Search notifications (Super Admin only)
   * @param {Object} params - Search parameters
   */
  async searchNotifications(params) {
    return apiClient.get(`${this.baseUrl}/admin/notifications/search`, { params });
  }

  /**
   * Filter notifications (Super Admin only)
   * @param {Object} filters - Filter criteria
   */
  async filterNotifications(filters) {
    return apiClient.post(`${this.baseUrl}/admin/notifications/filter`, filters);
  }

  /**
   * Get notifications by date range (Super Admin only)
   * @param {string} startDate - Start date (ISO format)
   * @param {string} endDate - End date (ISO format)
   */
  async getNotificationsByDateRange(startDate, endDate) {
    return apiClient.get(`${this.baseUrl}/admin/notifications/date-range`, {
      params: { startDate, endDate },
    });
  }

  /**
   * Clean up old notifications (Super Admin only)
   * @param {number} daysToKeep - Delete notifications older than this many days (min 30)
   */
  async cleanupOldNotifications(daysToKeep = 90) {
    return apiClient.post(`${this.baseUrl}/admin/notifications/cleanup`, { daysToKeep });
  }

  /**
   * Delete expired notifications (Super Admin only)
   */
  async deleteExpiredNotifications() {
    return apiClient.delete(`${this.baseUrl}/admin/notifications/expired`);
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Get notification type color for UI
   * @param {string} type - Notification type
   * @returns {string} Tailwind CSS color class
   */
  getNotificationTypeColor(type) {
    const colors = {
      info: 'blue',
      success: 'green',
      warning: 'orange',
      error: 'red',
      promotion: 'purple',
      order: 'cyan',
      account: 'indigo',
      system: 'gray',
    };
    return colors[type] || 'blue';
  }

  /**
   * Get notification type icon for UI
   * @param {string} type - Notification type
   * @returns {string} Icon name
   */
  getNotificationTypeIcon(type) {
    const icons = {
      info: 'Info',
      success: 'CheckCircle',
      warning: 'AlertTriangle',
      error: 'XCircle',
      promotion: 'Gift',
      order: 'ShoppingBag',
      account: 'User',
      system: 'Settings',
    };
    return icons[type] || 'Bell';
  }

  /**
   * Get notification priority badge color
   * @param {string} priority - Notification priority
   * @returns {string} Tailwind CSS color class
   */
  getNotificationPriorityColor(priority) {
    const colors = {
      low: 'gray',
      medium: 'blue',
      high: 'orange',
      urgent: 'red',
    };
    return colors[priority] || 'gray';
  }

  /**
   * Format notification time for display
   * @param {string} date - ISO date string
   * @returns {string} Human readable time
   */
  formatNotificationTime(date) {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - notificationDate.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }

    if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }

    return notificationDate.toLocaleDateString();
  }

  /**
   * Group notifications by date for UI
   * @param {Array} notifications - Array of notification objects
   * @returns {Object} Grouped notifications
   */
  groupNotificationsByDate(notifications) {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    notifications.forEach((notification) => {
      const notificationDate = new Date(notification.createdAt);
      notificationDate.setHours(0, 0, 0, 0);

      if (notificationDate.getTime() === today.getTime()) {
        groups.today.push(notification);
      } else if (notificationDate.getTime() === yesterday.getTime()) {
        groups.yesterday.push(notification);
      } else if (notificationDate > weekAgo) {
        groups.thisWeek.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  }

  /**
   * Mark multiple notifications as read (convenience method)
   * @param {number[]} ids - Array of notification IDs
   * @returns {Promise} Promise that resolves when all are marked as read
   */
  async markMultipleAsRead(ids) {
    const promises = ids.map((id) => this.markAsRead(id));
    return Promise.all(promises);
  }

  /**
   * Delete multiple notifications (convenience method)
   * @param {number[]} ids - Array of notification IDs
   * @returns {Promise} Promise that resolves when all are deleted
   */
  async deleteMultiple(ids) {
    return this.bulkDeleteNotifications(ids);
  }

  /**
   * Archive multiple notifications (convenience method)
   * @param {number[]} ids - Array of notification IDs
   * @returns {Promise} Promise that resolves when all are archived
   */
  async archiveMultiple(ids) {
    const promises = ids.map((id) => this.markAsArchived(id));
    return Promise.all(promises);
  }

  /**
   * Get notification summary for dashboard
   * @returns {Promise} Notification summary data
   */
  async getNotificationSummary() {
    try {
      const [recent, unreadCount] = await Promise.all([
        this.getRecentNotifications(5),
        this.getUnreadCount(),
      ]);

      return {
        recentNotifications: recent.data?.notifications || [],
        unreadCount: unreadCount.data?.unreadCount || 0,
      };
    } catch (error) {
      console.error('Failed to fetch notification summary:', error);
      return {
        recentNotifications: [],
        unreadCount: 0,
      };
    }
  }

  /**
   * Poll for new notifications (for real-time updates)
   * @param {number} lastCheckTimestamp - Last check timestamp
   * @returns {Promise} New notifications
   */
  async pollNewNotifications(lastCheckTimestamp) {
    const params = {
      fromDate: new Date(lastCheckTimestamp).toISOString(),
      limit: 50,
    };

    const response = await this.getUserNotifications(params);
    return response.data?.notifications || [];
  }
}

export const notificationService = new NotificationService();